import React, { useCallback, useEffect, useId, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { ShieldCheck, X } from 'lucide-react';
import { useI18n } from '../../i18n/I18nProvider';
import { acknowledgeLegal, answerPrivacyReview, fetchLegalStatus, type LegalStatus } from './api';
import { setLegalNoticeShowing } from './noticeStore';
import './legal.css';

const LATER_KEY = 'kudos.legalNotice.later';

function laterThisVisit(): boolean {
  try { return sessionStorage.getItem(LATER_KEY) === '1'; } catch { return false; }
}

function rememberLater() {
  try { sessionStorage.setItem(LATER_KEY, '1'); } catch { /* private window: it comes back on the next page */ }
}

/**
 * The first-sign-in notice (privacy plan item 3), and once it is answered the one-time
 * "Review what colleagues see" prompt for people who had an account before private by
 * default (item 4).
 *
 * Not blocking: a card at the bottom of the page, the app usable around it. Acknowledging
 * is a deliberate act: only "I understand" records it (with the two versions read). Closing
 * it records nothing; it comes back on the next visit, and again whenever the notice or the
 * terms get a new version. That follows the transparency guidance: the notice is shown, and
 * the record says it was read, never that closing it meant agreement.
 */
export default function LegalNotice({ readOnly = false }: { readOnly?: boolean }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const titleId = useId();
  const [status, setStatus] = useState<LegalStatus | null>(null);
  const [later, setLater] = useState(laterThisVisit);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    fetchLegalStatus().then((s) => { if (live) setStatus(s); }).catch(() => {});
    return () => { live = false; };
  }, []);

  // The legal pages themselves are where the links lead: no card on top of them.
  const onLegalPage = pathname.startsWith('/legal/');
  const needsAck = !!status && !status.acknowledged;
  const needsReview = !!status && status.acknowledged && status.privacy_review_due;
  const visible = !readOnly && !later && !onLegalPage && (needsAck || needsReview);

  useEffect(() => {
    setLegalNoticeShowing(visible);
    return () => setLegalNoticeShowing(false);
  }, [visible]);

  const acknowledge = useCallback(async () => {
    if (!status) return;
    setBusy(true);
    setFailed(false);
    try {
      setStatus(await acknowledgeLegal(status.notice_version, status.terms_version));
    } catch (e: any) {
      // A version that changed while the card was open: show the new one.
      if (e?.status === 409) fetchLegalStatus().then(setStatus).catch(() => {});
      else setFailed(true);
    } finally {
      setBusy(false);
    }
  }, [status]);

  const review = useCallback(async (outcome: 'reviewed' | 'kept') => {
    setBusy(true);
    setFailed(false);
    try {
      setStatus(await answerPrivacyReview(outcome));
      if (outcome === 'reviewed') navigate('/profile/edit#privacy');
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }, [navigate]);

  if (!visible || !status) return null;

  const close = () => { rememberLater(); setLater(true); };

  if (needsAck) {
    return (
      <section className="kln" aria-labelledby={titleId} data-bottom-bar="">
        <span className="kln__icon" aria-hidden="true"><ShieldCheck size={18} /></span>
        <div className="kln__text">
          <h2 id={titleId} className="kln__title">{status.first_time ? t('legal.notice.title') : t('legal.notice.changedTitle')}</h2>
          <ul className="kln__points">
            <li>{t('legal.notice.line1')}</li>
            <li>{t('legal.notice.line2')}</li>
            <li>{t('legal.notice.line3')}</li>
          </ul>
          <p className="kln__links">
            <Link to="/legal/privacy">{t('legal.privacy')}</Link>
            <span aria-hidden="true"> · </span>
            <Link to="/legal/terms">{t('legal.terms')}</Link>
          </p>
          {failed && <p className="kln__error" role="alert">{t('legal.notice.failed')}</p>}
          <div className="kln__actions">
            <button type="button" className="kln__btn kln__btn--primary" onClick={acknowledge} disabled={busy}>
              {t('legal.notice.understand')}
            </button>
          </div>
        </div>
        <button type="button" className="kln__close" onClick={close} aria-label={t('legal.notice.later')}>
          <X size={16} aria-hidden="true" />
        </button>
      </section>
    );
  }

  const shown = status.shown || { email: true, points: true, kudos: true, birthday: false };
  const on = [
    shown.email && t('legal.review.email'),
    shown.points && t('legal.review.points'),
    shown.kudos && t('legal.review.kudos'),
    shown.birthday && t('legal.review.birthday'),
  ].filter(Boolean) as string[];

  return (
    <section className="kln" aria-labelledby={titleId} data-bottom-bar="">
      <span className="kln__icon" aria-hidden="true"><ShieldCheck size={18} /></span>
      <div className="kln__text">
        <h2 id={titleId} className="kln__title">{t('legal.review.title')}</h2>
        <p className="kln__body">{t('legal.review.body')}</p>
        <p className="kln__body">
          {on.length > 0 ? t('legal.review.current', { items: on.join(', ') }) : t('legal.review.nothing')}
        </p>
        {failed && <p className="kln__error" role="alert">{t('legal.notice.failed')}</p>}
        <div className="kln__actions">
          <button type="button" className="kln__btn kln__btn--primary" onClick={() => review('reviewed')} disabled={busy}>
            {t('legal.review.review')}
          </button>
          <button type="button" className="kln__btn" onClick={() => review('kept')} disabled={busy}>
            {t('legal.review.keep')}
          </button>
        </div>
      </div>
    </section>
  );
}
